import {
  Box,
  Button,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import pic from "assets/discover/yellow_white.webp";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { Plans } from "schemas/payments";

import { NEW_SUBSCRIBER_PROMO_PERCENTAGE } from "constants/influencer";
import { DISPLAYED_ONBOARDING_PAYWALL } from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

const OnboardingPaywall = () => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, setOpenCheckoutDialog, setUpgradeDialogSource, subscription } =
    useContext(SubscriptionContext);

  const hideConciergePlan = true;

  const handleCheckout = (plan: Plans) => {
    trackEvent("Onboarding Paywall - Upgrade Button Clicked", { plan });
    const state: CheckoutState = {
      plan,
      source: "Onboarding Paywall",
    };
    setOpenCheckoutDialog(state);
  };

  const closeDialog = () => {
    trackEvent("Onboarding Paywall - Close Button Clicked");
    localStorage.setItem(
      `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
      "true",
    );
    setOpenCheckoutDialog(null);
    setUpgradeDialogSource("");
  };

  const renderDiscount = () => {
    return subscription?.newSubscriberPromoDate ? (
      <Typography sx={styles.discount} textAlign="center">
        <em>
          <strong>
            {NEW_SUBSCRIBER_PROMO_PERCENTAGE}% off until{" "}
            {subscription?.newSubscriberPromoDate}
          </strong>{" "}
          (auto-applied at checkout)
        </em>
      </Typography>
    ) : (
      <></>
    );
  };

  return (
    <DialogContent
      sx={{
        backgroundImage: `url(${pic})`,
        backgroundPosition: "center",
        backgroundSize: "200% 200%",
        px: 3,
        py: 6,
      }}
    >
      <IconButton sx={styles.closeButton} onClick={closeDialog}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <Typography textAlign="center" variant="h4" gutterBottom>
        Unleash the full power of Bento
      </Typography>
      <Typography textAlign={"center"} sx={{ mb: 3, fontSize: 18 }}>
        <em>
          The all-in-one sales tool; your brand database, CRM and sales
          automation.
        </em>
      </Typography>

      <Grid container gap={2} justifyContent={"center"}>
        <Grid item xs={12} md={5} sx={styles.paper}>
          <Grid
            container
            gap={4}
            flexDirection={"column"}
            justifyContent={"space-between"}
          >
            <Box>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Typography variant="h6">
                  <strong>Pro</strong>
                </Typography>

                <Box>
                  <Box sx={{ color: grey[800] }}>
                    Starts at <br />
                  </Box>{" "}
                  <strong>
                    <Box component="span" sx={{ fontSize: 20 }}>
                      ${plans?.[Plans.PRO_YEARLY]?.price}
                    </Box>
                    <Box component="span" sx={{ color: grey[500] }}>
                      /mo
                    </Box>
                  </strong>
                </Box>
              </Grid>
              <Typography sx={styles.text}>
                With Bento's Pro plan,{" "}
                <strong>
                  send {plans?.[Plans.PRO]?.monthly_limit} pitches a month,
                </strong>{" "}
                target the right brands with smart filters, see who's most
                likely to reply, and get fresh recommendations - making your
                outreach effortless.
              </Typography>
            </Box>

            <Box>
              <Typography>
                🌟 🎯 Pro users are <strong>11x more likely </strong> to land a
                deal than on our free plans.
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => handleCheckout(Plans.PRO_YEARLY)}
              >
                Upgrade Now{" "}
                <Box
                  sx={{ ml: 1 }}
                  component="i"
                  className="fa-regular fa-arrow-right"
                />
              </Button>
              {renderDiscount()}
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12} md={5} sx={styles.paper}>
          <Grid
            container
            gap={4}
            flexDirection={"column"}
            justifyContent={"space-between"}
          >
            <Box>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Typography variant="h6">
                  <strong>AI Virtual Assistant</strong>
                </Typography>

                <Box>
                  <Box sx={{ color: grey[800] }}>
                    Starts at <br />
                  </Box>{" "}
                  <strong>
                    <Box component="span" sx={{ fontSize: 20 }}>
                      ${plans?.[Plans.VA_YEARLY]?.price}
                    </Box>
                    <Box component="span" sx={{ color: grey[300] }}>
                      /mo
                    </Box>
                  </strong>
                </Box>
              </Grid>

              <Typography sx={styles.text}>
                With Bento's AI Virtual Assistant plan, you get everything in
                Pro plus a true <strong>hands-off experience</strong> -- our AI
                sends 300 pitches for you every month, across multiple niches at
                once (swap them out anytime). And with tier-one customer
                support, you'll always have fast, human help when you need it.
              </Typography>
            </Box>

            <Box>
              <Typography>
                🌟 🎯 AI Virtual Assistants users are{" "}
                <strong>33x more likely</strong> to land a deal than on our free
                plans.
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 2 }}
                onClick={() => handleCheckout(Plans.VA_YEARLY)}
              >
                Upgrade Now{" "}
                <Box
                  sx={{ ml: 1 }}
                  component="i"
                  className="fa-regular fa-arrow-right"
                />
              </Button>

              {renderDiscount()}
            </Box>
          </Grid>
        </Grid>

        {!hideConciergePlan && (
          <Grid item xs={12} md={5} sx={styles.paper}>
            <Grid
              container
              gap={4}
              flexDirection={"column"}
              justifyContent={"space-between"}
            >
              <Box>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3 }}
                >
                  <Typography variant="h6">
                    <strong>Bento Launchpad</strong>
                  </Typography>

                  <Box>
                    <Box sx={{ color: grey[800] }}>
                      Starts at <br />
                    </Box>{" "}
                    <strong>
                      <Box component="span" sx={{ fontSize: 20 }}>
                        ${plans?.[Plans.CONCIERGE_YEARLY]?.price}
                      </Box>
                      <Box component="span" sx={{ color: grey[300] }}>
                        /mo
                      </Box>
                    </strong>
                  </Box>
                </Grid>

                <Typography sx={styles.text}>
                  Bento's most hands-on plan to help you land your first paid
                  collaborations. Receive{" "}
                  <strong>300 automated pitches each month</strong>, a
                  personalized brand strategy plan, and access to{" "}
                  <strong>live group coaching</strong> for tactical growth
                  support. When brands respond, you'll{" "}
                  <strong>get real-time help drafting replies</strong>, shaping
                  offers, and negotiating terms so you can close deals
                  confidently.
                </Typography>
              </Box>

              <Box>
                <Typography>
                  🌟🤝 Bento Launchpad users consistently build stronger,
                  longer-lasting brand relationships
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => handleCheckout(Plans.CONCIERGE_YEARLY)}
                >
                  Upgrade Now{" "}
                  <Box
                    sx={{ ml: 1 }}
                    component="i"
                    className="fa-regular fa-arrow-right"
                  />
                </Button>

                {renderDiscount()}
              </Box>
            </Grid>
          </Grid>
        )}
      </Grid>
    </DialogContent>
  );
};

export default OnboardingPaywall;
